import React, { useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useSession } from "../store/session";
import { useI18n } from "../i18n";
import { resendVerification } from "../api/auth";
import { ApiError } from "../api/http";


const LoginPage: React.FC = () => {
  const { login, requestMagicLink } = useSession();
  const navigate = useNavigate();
  const { t, locale, setLocale, locales } = useI18n();

  const [mode, setMode] = useState<"password" | "magic">("password");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canMagic = useMemo(() => typeof requestMagicLink === "function", [requestMagicLink]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setSent(false);

    try {
      if (mode === "magic") {
        if (!canMagic) throw new Error("Magic link is not enabled.");
        await requestMagicLink(email);
        setSent(true);
        return;
      }

      await login(email, password);
      navigate("/");

    } catch (err: any) {
      if (err instanceof ApiError) {
        if (err.status === 403 && err.code === "email_not_verified") {
          setError(t("auth.emailNotVerified"));
        } else if (err.status === 429) {
          setError(t("common.rateLimited"));
        } else {
          setError(err.message || "Login failed");
        }
      } else {
        setError(err?.message || "Login failed");
      }
    } finally {
      setSubmitting(false);
    }



  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-slate-900/80 border border-slate-800 rounded-2xl shadow-2xl p-6 space-y-6">
        {/* Header + language switcher */}
          <div className="flex items-center gap-2 text-xs">
          <select
            value={locale}
            onChange={(e) => setLocale(e.target.value as any)}
            className="px-2 py-1 rounded-xl border border-slate-700 bg-slate-950/60 text-slate-200 text-[11px] focus:outline-none focus:ring-2 focus:ring-pink-500"
          >
            {locales.map((l) => (
              <option key={l.code} value={l.code}>
                {l.nativeName} ({l.name})
              </option>
            ))}
          </select>

          </div>

        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-2xl bg-pink-500/20 border border-pink-500/50 flex items-center justify-center text-pink-300 font-bold text-sm">
              IS
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.15em] text-pink-300">
                {t("auth.welcomeBack")}
              </div>
              <h1 className="text-lg font-semibold text-slate-50 mt-1">
                {t("auth.loginTitle")}
              </h1>
              <p className="text-xs text-slate-400 mt-1">
                {t("auth.loginSubtitle")}
              </p>
            </div>
          </div>

        </div>

        {/* Mode switch */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setMode("password")}
            className={
              "px-3 py-1.5 rounded-full text-xs border " +
              (mode === "password"
                ? "border-pink-500 bg-pink-500/10 text-pink-200"
                : "border-slate-700 text-slate-300 hover:border-slate-500")
            }
          >
            {t("auth.modePassword")}
          </button>
          <button
            type="button"
            onClick={() => setMode("magic")}
            className={
              "px-3 py-1.5 rounded-full text-xs border " +
              (mode === "magic"
                ? "border-pink-500 bg-pink-500/10 text-pink-200"
                : "border-slate-700 text-slate-300 hover:border-slate-500")
            }
          >
            {t("auth.modeMagicLink")}
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-slate-300">
              {t("common.email")}
            </label>
            <input
              type="email"
              required
              autoComplete="email"
              className="w-full rounded-xl bg-slate-950/70 border border-slate-700 px-3 py-2 text-sm text-slate-50 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
            />
          </div>

          {mode === "password" && (
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-slate-300">
                {t("common.password")}
              </label>
              <input
                type="password"
                required
                autoComplete="current-password"
                className="w-full rounded-xl bg-slate-950/70 border border-slate-700 px-3 py-2 text-sm text-slate-50 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
              />
            </div>
          )}

          {sent && (  <div className="text-xs text-emerald-300 bg-emerald-950/30 border border-emerald-800 rounded-lg px-3 py-2">
          {mode === "magic" ? t("auth.magicSent") : t("auth.verificationSent")}
          </div>
          )}

          {error && (
            <div className="text-xs text-rose-400 bg-rose-950/40 border border-rose-800 rounded-lg px-3 py-2">
              {error}
            </div>
          )}
          {error && error === t("auth.emailNotVerified") && (
          <button
             type="button" onClick={async () => {
               try {
                 await resendVerification(email);
                 setError(null);
                 setSent(true);
               } catch (e: any) {
                 if (e instanceof ApiError) {
                   if (e.status === 429) setError(t("common.rateLimited"));
                   else setError(e.message || "Failed to resend verification email");
                 } else {
                   setError(e?.message || "Failed to resend verification email");
                 }
               }
            }}
                className="w-full mt-2 inline-flex items-center justify-center rounded-xl border border-slate-700 text-slate-200 text-sm py-2 hover:border-slate-500"  >
                {t("auth.resendVerification")}
              </button>
            )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-pink-500 text-white text-sm font-semibold py-2.5 shadow-lg shadow-pink-500/30 hover:bg-pink-400 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitting
              ? t("common.loading")
              : mode === "magic"
              ? t("auth.sendMagicLink")
              : t("auth.loginButton")}
          </button>
        </form>

        <div className="text-xs text-slate-400 flex items-center justify-between pt-2">
          <span>{t("auth.noAccount")}</span>
          <Link to="/register" className="text-pink-300 hover:text-pink-200 font-medium">
            {t("auth.registerCta")}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
